import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  ChartBarIcon,
  BoltIcon,
  BanknotesIcon,
  InformationCircleIcon,
  ClockIcon,
  CalculatorIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import CompanyLogo from '../components/common/CompanyLogo';
import { calculateValuationPeriod } from '../utils/valuationPeriodCalculator';

interface FacilityInfo {
  facilityId: string;
  companyName: string;
  ticker: string;
  discountRate: number;
  lastTradePrice: number;
  maxVWAPPurchaseShares: number;
  maxVWAPPurchaseDollar: number;
  maxRapidPurchaseShares: number;
  maxRapidPurchaseDollar: number;
  advanceDrawAvailable: number;
  marketStatus: string;
}

const ExerciseNotice: React.FC = () => {
  const navigate = useNavigate();
  const { type } = useParams<{ type: string }>();
  const [facilityInfo, setFacilityInfo] = useState<FacilityInfo | null>(null);
  const [shareAmount, setShareAmount] = useState<string>('');
  const [dollarAmount, setDollarAmount] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Helper functions
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };



  // Get user facility info
  useEffect(() => {
    const fetchFacilityInfo = async () => {
      try {
        const userStr = localStorage.getItem('user');
        const token = localStorage.getItem('token');
        
        if (!userStr || !token) {
          navigate('/login');
          return;
        }

        const user = JSON.parse(userStr);
        const facilityId = user.facilityId;

        if (!facilityId) {
          setError('No active facility found');
          setIsLoading(false);
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/dashboard/${facilityId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (response.data.success) {
          setFacilityInfo({
            facilityId,
            ...response.data.data
          });
        }
      } catch (err) {
        console.error('Error fetching facility info:', err);
        setError('Failed to load facility information');
      } finally {
        setIsLoading(false);
      }
    };

    fetchFacilityInfo();
  }, [navigate]);

  // Calculate dollar amount when shares change
  const handleSharesChange = (value: string) => {
    // Remove commas and non-numeric characters for calculation
    const cleanValue = value.replace(/[^\d]/g, '');
    setShareAmount(cleanValue);
    if (cleanValue && facilityInfo) {
      const shares = parseFloat(cleanValue);
      const dollars = shares * facilityInfo.lastTradePrice;
      setDollarAmount(dollars.toFixed(2));
    } else {
      setDollarAmount('');
    }
  };

  // Calculate share amount when dollars change
  const handleDollarsChange = (value: string) => {
    setDollarAmount(value);
    if (value && facilityInfo) {
      const dollars = parseFloat(value);
      const shares = Math.floor(dollars / facilityInfo.lastTradePrice);
      setShareAmount(shares.toString());
    } else {
      setShareAmount('');
    }
  };

  const getMaximums = () => {
    if (!facilityInfo) return { maxShares: 0, maxDollars: 0 };
    
    switch (type) {
      case 'vwap':
        return {
          maxShares: facilityInfo.maxVWAPPurchaseShares,
          maxDollars: facilityInfo.maxVWAPPurchaseDollar
        };
      case 'rapid':
        return {
          maxShares: facilityInfo.maxRapidPurchaseShares,
          maxDollars: facilityInfo.maxRapidPurchaseDollar
        };
      case 'advance':
        return {
          maxShares: Math.floor(facilityInfo.advanceDrawAvailable / facilityInfo.lastTradePrice),
          maxDollars: facilityInfo.advanceDrawAvailable
        };
      default:
        return { maxShares: 0, maxDollars: 0 };
    }
  };

  const getSettlementPeriod = () => {
    switch (type) {
      case 'vwap':
        const vwapInfo = calculateValuationPeriod('VWAP');
        return `${vwapInfo.valuationPeriod} - ${vwapInfo.settlementPeriod}`;
      case 'rapid':
        const rapidInfo = calculateValuationPeriod('RAPID');
        return `${rapidInfo.valuationPeriod} - ${rapidInfo.settlementPeriod}`;
      case 'advance':
        return 'Immediate funding upon approval';
      default:
        return '';
    }
  };

  const handleSubmit = () => {
    if (!shareAmount || !facilityInfo) return;

    const shares = parseInt(shareAmount);
    const { maxShares } = getMaximums();

    // Validate amount doesn't exceed maximum
    if (shares > maxShares) {
      setError(`Amount exceeds maximum eligible shares of ${maxShares.toLocaleString()}`);
      return;
    }

    setError('');
    setShowConfirmation(true);
  };

  const handleConfirmSubmit = async () => {
    if (!termsAccepted || !shareAmount || !facilityInfo) return;

    setIsSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const shares = parseInt(shareAmount);

      const transactionType = type === 'vwap' ? 'VWAP' : 
                             type === 'rapid' ? 'Rapid' : 
                             'Advance_Draw';

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/transactions/exercise-notice`,
        {
          facilityId: facilityInfo.facilityId,
          transactionType,
          sharesRequested: shares,
          exerciseTime: new Date().toISOString()
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        // Navigate to success page or back to dashboard
        navigate('/dashboard', { 
          state: { 
            message: `${type?.toUpperCase()} Purchase exercise notice submitted successfully!`,
            transactionId: response.data.data.transactionId
          }
        });
      }
    } catch (err: any) {
      console.error('Error submitting exercise notice:', err);
      setError(err.response?.data?.message || 'Failed to submit exercise notice');
      setShowConfirmation(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-700 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading facility information...</p>
        </div>
      </div>
    );
  }

  if (!facilityInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">{error || 'No facility information available'}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-navy-700 text-white rounded-lg hover:bg-navy-800"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const { maxShares } = getMaximums();
  const settlementPeriod = getSettlementPeriod();

  const typeConfig = {
    vwap: {
      title: 'VWAP Purchase',
      icon: ChartBarIcon,
      color: 'blue',
      description: 'Volume Weighted Average Price purchase over 3 trading days'
    },
    rapid: {
      title: 'Rapid Purchase',
      icon: BoltIcon,
      color: 'purple',
      description: 'Quick purchase settling in 1 trading session'
    },
    advance: {
      title: 'Advance Draw',
      icon: BanknotesIcon,
      color: 'green',
      description: 'Immediate funding against future exercises'
    }
  };

  const config = typeConfig[(type as 'vwap' | 'rapid' | 'advance') || 'vwap'];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-8"
        >
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <div className={`p-3 bg-${config.color}-100 rounded-lg`}>
              <config.icon className={`h-8 w-8 text-${config.color}-600`} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{config.title} Exercise Notice</h1>
              <p className="text-gray-600">{config.description}</p>
            </div>
          </div>

          {/* Market Status */}
          {facilityInfo.marketStatus !== 'open' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-center gap-3">
              <ClockIcon className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="text-sm font-medium text-yellow-800">
                  Market is currently {facilityInfo.marketStatus}
                </p>
                <p className="text-xs text-yellow-600">
                  Your exercise will be processed when the market opens
                </p>
              </div>
            </div>
          )}

          {/* Company Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-4">
              <CompanyLogo 
                ticker={facilityInfo.ticker} 
                companyName={facilityInfo.companyName} 
                size="lg" 
              />
              <div className="flex-1 grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <p className="text-sm text-gray-500">Company</p>
                  <p className="font-medium">{facilityInfo.companyName} ({facilityInfo.ticker})</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Current Price</p>
                  <p className="font-medium">${facilityInfo.lastTradePrice.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Discount Rate</p>
                  <p className="font-medium">{facilityInfo.discountRate}%</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Settlement Period</p>
                  <p className="font-medium">{settlementPeriod}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Exercise Amount */}
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Number of Shares
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={shareAmount ? parseInt(shareAmount).toLocaleString() : ''}
                  onChange={(e) => handleSharesChange(e.target.value)}
                  placeholder="0"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500">
                  Max: {maxShares.toLocaleString()}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dollar Amount
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={dollarAmount}
                  onChange={(e) => handleDollarsChange(e.target.value)}
                  placeholder="0.00"
                  max={getMaximums().maxDollars}
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500">
                  Max: ${getMaximums().maxDollars.toLocaleString()}
                </div>
              </div>
            </div>
          </div>

          {/* Maximum Eligibility Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <InformationCircleIcon className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-900 mb-1">Maximum Eligibility</p>
                <p className="text-blue-700">
                  Based on your facility terms and current market conditions, you are eligible to exercise up to{' '}
                  <span className="font-medium">{maxShares.toLocaleString()} shares</span> (
                  <span className="font-medium">${getMaximums().maxDollars.toLocaleString()}</span>) for this {config.title}.
                </p>
                {type !== 'advance' && (
                  <p className="text-blue-600 mt-1">
                    This limit is based on {type === 'vwap' ? '10%' : '30%'} of the 5-day average trading volume.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!shareAmount || parseFloat(shareAmount) <= 0 || isSubmitting}
              className={`flex-1 px-6 py-3 bg-${config.color}-600 text-white rounded-lg hover:bg-${config.color}-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <CalculatorIcon className="h-5 w-5" />
                  Submit Exercise Notice
                </>
              )}
            </button>
          </div>

          {/* Terms Notice */}
          <p className="text-xs text-gray-500 mt-4 text-center">
            By submitting this exercise notice, you agree to the terms of your equity facility agreement.
            The final purchase price will be calculated based on the {type === 'advance' ? '10-day' : '5-day'} VWAP
            less your facility discount rate of {facilityInfo.discountRate}%.
          </p>
        </motion.div>

        {/* Confirmation Dialog */}
        {showConfirmation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-bold text-gray-900">Confirm Exercise Notice</h3>
                <p className="text-sm text-gray-600 mt-1">Please review and confirm your exercise notice details</p>
              </div>

              <div className="p-6 space-y-4">
                {/* Exercise Notice Preview */}
                <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                  <div className="text-center border-b border-gray-200 pb-4">
                    <h4 className="text-lg font-semibold text-gray-900">EXERCISE NOTICE</h4>
                    <p className="text-sm text-gray-600">{config.title}</p>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Company:</span>
                      <span className="font-medium">{facilityInfo.companyName} ({facilityInfo.ticker})</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Exercise Type:</span>
                      <span className="font-medium">{config.title}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Number of Shares:</span>
                      <span className="font-medium">{parseInt(shareAmount).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Current Price:</span>
                      <span className="font-medium">${facilityInfo.lastTradePrice.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Estimated Amount:</span>
                      <span className="font-medium">{formatCurrency(parseFloat(dollarAmount))}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Discount Rate:</span>
                      <span className="font-medium">{facilityInfo.discountRate}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Settlement Period:</span>
                      <span className="font-medium">{getSettlementPeriod()}</span>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-600">
                      The final purchase price will be calculated based on the {type === 'advance' ? '10-day' : '5-day'} VWAP
                      less the facility discount rate of {facilityInfo.discountRate}%.
                    </p>
                  </div>
                </div>

                {/* Terms Acceptance */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">
                      I confirm that I have reviewed the exercise notice details above and agree to the terms of the 
                      equity facility agreement. I understand that this exercise notice is binding upon submission.
                    </span>
                  </label>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-gray-200 flex gap-3">
                <button
                  onClick={() => {
                    setShowConfirmation(false);
                    setTermsAccepted(false);
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmSubmit}
                  disabled={!termsAccepted || isSubmitting}
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Submitting...
                    </>
                  ) : (
                    'Confirm & Submit Notice'
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExerciseNotice; 